import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { categories, articles, search, comments, versions, SearchFilters } from "@/lib/kbApi";
import type { CreateCommentRequest, UpdateCommentRequest } from "@/types/api";

// Categories
export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () => categories.list(),
  });
}

// Articles
export function useArticles(categorySlug?: string) {
  return useQuery({
    queryKey: ["articles", { category: categorySlug }],
    queryFn: () => articles.list({ category: categorySlug }),
  });
}

export function useArticle(slug: string) {
  return useQuery({
    queryKey: ["article", slug],
    queryFn: () => articles.getBySlug(slug),
    enabled: !!slug,
  });
}

// Search
export function useSearch(query: string, filters?: SearchFilters) {
  return useQuery({
    queryKey: ["search", query, filters],
    queryFn: () => search.query(query, filters),
    enabled: !!query.trim(),
  });
}

export function useSearchSuggestions(query: string) {
  return useQuery({
    queryKey: ["search-suggestions", query],
    queryFn: () => search.suggestions(query),
    enabled: !!query.trim() && query.length > 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Comments
export function useComments(articleSlug: string) {
  return useQuery({
    queryKey: ["comments", articleSlug],
    queryFn: () => comments.list(articleSlug),
    enabled: !!articleSlug,
  });
}

export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleSlug, data }: { articleSlug: string; data: CreateCommentRequest }) =>
      comments.create(articleSlug, data),
    onSuccess: (_, { articleSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", articleSlug] });
    },
  });
}

export function useReplyComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, data, articleSlug }: { commentId: number; data: CreateCommentRequest; articleSlug: string }) =>
      comments.reply(commentId, data),
    onSuccess: (_, { articleSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", articleSlug] });
    },
  });
}

export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, data, articleSlug }: { commentId: number; data: UpdateCommentRequest; articleSlug: string }) =>
      comments.update(commentId, data),
    onSuccess: (_, { articleSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", articleSlug] });
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, articleSlug }: { commentId: number; articleSlug: string }) =>
      comments.delete(commentId),
    onSuccess: (_, { articleSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", articleSlug] });
    },
  });
}

export function useAddEmojiReaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, emoji, articleSlug }: { commentId: number; emoji: string; articleSlug: string }) =>
      comments.addReaction(commentId, emoji),
    onSuccess: (_, { articleSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", articleSlug] });
    },
  });
}

export function useRemoveEmojiReaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, emoji, articleSlug }: { commentId: number; emoji: string; articleSlug: string }) =>
      comments.removeReaction(commentId, emoji),
    onSuccess: (_, { articleSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", articleSlug] });
    },
  });
}

// Versions
export function useVersions(articleId: number) {
  return useQuery({
    queryKey: ["versions", articleId],
    queryFn: () => versions.list(articleId),
    enabled: !!articleId,
  });
}

// Article mutations
export function useCreateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: articles.create,
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useUpdateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: any }) =>
      articles.update(slug, data),
    onSuccess: (result, { slug }) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["article", slug] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: articles.delete,
    onSuccess: (result, slug) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      // Remove the deleted article from cache
      queryClient.removeQueries({ queryKey: ["article", slug] });
    },
  });
}